import def from "csi/def";
import FieldConstructor, { Field } from "csi/def/Field";
import { FormView } from "csi/form/View";
import locale from "csi/locale";
import csi from "csi/main";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import _ from "csi/_base/underscore";
import { Context } from "csi/def/Context";
import SelectBOMItemProductForm from "../SelectBOMItemProductForm";
const PRODUCT_TYPE = "ProductType";
const MATERIAL_TYPE = "MaterialType";

const SelectFoodBOMProductForm = declare([SelectBOMItemProductForm], {

	/** @property {QueryResult} bomSectionQueryResult - The QueryResult object if action is performed in a BOM Section */
	bomSectionQueryResult: null,

	/** @property {string} bomSectionDefintionURL - The URL of the BOMSectionDefinition */
	bomSectionDefintionURL: "",

	/**
	 * @override
	 * Insert Product Type and Material Type fields
	 */
	_resolveView() {
		// Create other subtype fields first
		this.inherited(arguments);

		const fieldMaterialSubtypes = lang.mixin(new FieldConstructor(), {
			m_label: locale.resource(MATERIAL_TYPE),
			m_typeControl: def.FIELD_FILTERING_SELECT,
		});

		this.insertField(fieldMaterialSubtypes, 1);

		// cache BOMSectionDefinition url for filtering
		this.bomSectionDefintionURL = this.getBOMSectionDefinitionURL();
		const controller = this.getSourceController();
		controller.widget.set("bomSectionDefinitionURL", this.bomSectionDefintionURL); // set for table filters in AggregateFromMaterialTable.ts
	},

	/**
	 * @override
	 * Hide Material fields if "Product Type" field is empty
	 * @param fields 
	 */
	_buildFields(fields: Field[]) {
		this.inherited(arguments);

		const productType = this.defView.getFieldValue(PRODUCT_TYPE);
		this.setFieldHidden(locale.resource(MATERIAL_TYPE), !productType);
	},

	/**
	 * @override
	 * Hide subtype fields based on selected Product Type.
	 * @param formView 
	 * @param field 
	 * @param value 
	 */
	onChangeProductType(formView: FormView, field: Field, value: any) {
		this.inherited(arguments);

		// FoodBOM Specific logic
		const productType = xml.enumGetText(value);
		const ctx = this.getContext();

		const bomSectionDefinition = ctx.cache.getNode(this.bomSectionDefintionURL);
		const bomSecDefProductType = bomSectionDefinition ? xml.enumGetText(bomSectionDefinition.PlacementProductType) : null;
		// If BomSectionDefinition has a PlacementProductType, disable field
		if (bomSecDefProductType) {
			this.setFieldDisabled(locale.resource(PRODUCT_TYPE), true);
		}

		this.setFieldHidden(locale.resource(MATERIAL_TYPE), !(productType === "Material"));
	},

	/**
	 * @override
	 * Create field store depending on type of field
	 */
	getFieldStore(field, value) {
		if (field.m_label === locale.resource(MATERIAL_TYPE, [locale.typePlural(MATERIAL_TYPE)])){
			return this.getMaterialSubtypeStore(field);
		}
		return this.inherited(arguments);
	},

	/**
	 * Get field store for Material Type field
	 * @param field 
	 * @returns 
	 */
	getMaterialSubtypeStore(field: Field) {
		const MaterialUsageStandaloneEnum = xml.enumCreate("MaterialUsage", "Standalone"); 
		const q = new Query()
			.Attr("Published", xml.OP_EQUALS, true)
			.Attr("Available", xml.OP_EQUALS, true, "Child:Config")
			.Attr("MaterialUsage", xml.OP_EQUALS, MaterialUsageStandaloneEnum, "Child:Config");

		return this.getProductSubtypeStore(field, q, MATERIAL_TYPE);
	},

	/**
	 * @override
	 * Create field store for ProductSubtype field
	 * @param field 
	 */
	getProductSubtypeStore(field: Field, query: Query, productType: String) {
		// Filter by BOMSectionDefinition.PlacementProductTypes if action performed on BOMSection
		const ctx = this.getContext();

		const isValidBomSectionURL = this.bomSectionDefintionURL && this.bomSectionDefintionURL !== csi.EMPTY_URL;
		const placementProductTypes = isValidBomSectionURL ? ctx.cache.getNode(this.bomSectionDefintionURL).PlacementProductTypes : [];
		const qOr = query.Or();
		placementProductTypes.forEach((productType) => {
			const typeName = ctx.cache.getNode(productType).$Name;
			qOr.Attr("Node Name", xml.OP_EQUALS, typeName);
		});

		return this.inherited(arguments);
	},

	/**
	 * @override
	 * Get ItemsProductTypes allowed for FoodBOM
	 * @param field 
	 */
	getItemsProductTypes(ctx: Context) {
		let itemsProductTypes:Array<string> = this.inherited<Array<string>>(arguments);

		const bomSectionDefinition = ctx.cache.getNode(this.bomSectionDefintionURL);
		const bomSecDefProductType = bomSectionDefinition ? xml.enumGetText(bomSectionDefinition.PlacementProductType) : null;
		// If BomSectionDefinition has a PlacementProductType, use that for the store
		if (bomSecDefProductType) {
			itemsProductTypes = [bomSectionDefinition.PlacementProductType];
		}

		return itemsProductTypes;
	},

	/**
	 * Get bomSectionDefintion url
	 * @param field 
	 */
	getBOMSectionDefinitionURL() {
		let bomSectionDefinitionURL;
		const controller = this.getSourceController();
		
		const queryResult = controller.widget.getQueryResultByDomNode(controller.m_element);
		this.bomSectionQueryResult = queryResult ? queryResult : this.bomSectionQueryResult; // if not null update, else keep same value
		if (this.bomSectionQueryResult) {
			const groupParents = this.bomSectionQueryResult.getGroupParents();
			if (groupParents) {
				bomSectionDefinitionURL = groupParents[0].groupValue;
			}
		}

		return bomSectionDefinitionURL;
	},
    
});

export = SelectFoodBOMProductForm;